import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CompanyResponseDto,
  UnitListDto,
  WeightMeasurementUnit,
} from '@planning/app/api'
import { CompanyItemStore } from '@planning/rt-stores/externalDriver/CompanyItemStore'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { RailcarTrackPositionItemStore } from '@planning/rt-stores/railTrack/RailcarTrackPositionItemStore'
import { RailTrackItemStore } from '@planning/rt-stores/railTrack/RailTrackItemStore'
import { RailVisitItemStore } from '@planning/rt-stores/railVisit/RailVisitItemStore'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import _, { Dictionary } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { OrderListUploadComparisonStore } from './OrderListUploadComparisonStore'
import { OrderListUploadDataStore } from './OrderListUploadDataStore'
import { OrderListUploadGroupStore } from './OrderListUploadGroupStore'
import {
  BaplieParseResult,
  OrderListUploadParsingStore,
  VesselVisitBaplieParseResult,
} from './OrderListUploadParsingStore'

export interface IOrderDto extends UnitListDto {
  railTrack?: string
  position?: string
  customerName?: string
}

export interface IGeneralCargoUploadOrderDto {
  commodityId: number
  commodityName?: string
  referenceNumber?: string
  lotNumber?: string
  plannedAmount: number
  grossWeight?: number
  weightUnit?: WeightMeasurementUnit
  imoClasses: string[]
  identifiableItems: string[]
  railTrackId?: string
  railcarId?: number
  railTrackName?: string
  railcarNumber?: string
}

export interface IOrderList {
  carrierVisitId: number
  direction: CarrierVisitDirection
  orders?: Array<IOrderDto> | null
  remainOnboard?: Array<IOrderDto> | null
  nonNumeric?: Array<IOrderDto> | null
  vesselVisit?: VesselVisitBaplieParseResult
}

export class OrderListUploadViewStoreV2 {
  static readonly defaultHomePort = 'DEHAM'
  static readonly defaultListName = 'Default'

  open = false
  activeStep = 0

  vesselVisitId?: number
  railVisitId?: number
  direction: CarrierVisitDirection = CarrierVisitDirection.Inbound
  isRestow = false
  customer?: CompanyResponseDto

  dataStore: OrderListUploadDataStore
  comparisonStore: OrderListUploadComparisonStore
  parsingStore: OrderListUploadParsingStore
  orderGroupStore: OrderListUploadGroupStore

  acceptUpdates: Dictionary<boolean> = {}

  showVesselVisitMismatchWarning = false
  generalCargoUploadOrders: IGeneralCargoUploadOrderDto[] = []

  constructor(
    orderStore: OrderItemStore,
    private vesselVisitStore: VesselVisitItemStore,
    private railVisitStore: RailVisitItemStore,
    private railTrackStore: RailTrackItemStore,
    private railcarTrackPositionItemStore: RailcarTrackPositionItemStore,
    companyStore: CompanyItemStore,
  ) {
    makeObservable(this, {
      open: observable,
      activeStep: observable,
      vesselVisitId: observable,
      railVisitId: observable,
      direction: observable,
      customer: observable,
      acceptUpdates: observable,
      isRestow: observable,
      showVesselVisitMismatchWarning: observable,
      generalCargoUploadOrders: observable,

      selectedVisitId: computed,
      selectedCarrierType: computed,
      selectedVesselVisitItem: computed,
      vesselVisit: computed,
      railVisit: computed,
      orderUpdates: computed,
      totalCount: computed,
      issues: computed,
      listName: computed,
      isVesselVisitMismatch: computed,
      isGeneralCargoVisit: computed,

      setDirection: action,
      setCustomer: action,
      setOrdersFromUpdate: action,
      setFile: action,
      setActiveStep: action,
      setOpen: action,
      setAcceptUpdate: action,
      setAddToRemainOnBoard: action,
      setIsRestow: action,
      setShowVesselVisitMismatchWarning: action,
      setGeneralCargoUploadOrders: action,
      initAcceptUpdates: action,
    })

    this.dataStore = new OrderListUploadDataStore(
      this,
      orderStore,
      vesselVisitStore,
      railVisitStore,
    )
    this.comparisonStore = new OrderListUploadComparisonStore(
      this,
      this.railTrackStore,
      companyStore,
    )
    this.parsingStore = new OrderListUploadParsingStore(
      this,
      this.railTrackStore,
      this.railcarTrackPositionItemStore,
    )
    this.orderGroupStore = new OrderListUploadGroupStore(this)
  }

  get selectedVisitId() {
    return this.vesselVisitId ?? this.railVisitId
  }

  get selectedCarrierType() {
    if (this.vesselVisitId) return CarrierType.Vessel
    if (this.railVisitId) return CarrierType.Train

    return CarrierType.Universal
  }

  get listName() {
    return this.orderGroupStore.selectedGroup
  }

  get selectedVesselVisitItem() {
    if (!this.vesselVisitId || !_(this.vesselVisitStore.elements).has(this.vesselVisitId)) return

    return this.vesselVisitStore.elements[this.vesselVisitId]
  }

  get isVesselVisitMismatch() {
    if (!this.selectedVesselVisitItem || !this.dataStore.vesselVisitFromUpdate) return false

    const { vesselVisitFromUpdate } = this.dataStore
    const isMatching = this.selectedVesselVisitItem.vessels.some(vessel => {
      if (vesselVisitFromUpdate.imoNumber)
        return vessel.data.imoNumber === vesselVisitFromUpdate.imoNumber

      if (vesselVisitFromUpdate.callSign)
        return vessel.data.callSign === vesselVisitFromUpdate.callSign

      return vessel.data.name === vesselVisitFromUpdate.vesselName
    })

    return !isMatching
  }

  get isGeneralCargoVisit() {
    return (
      this.vesselVisit?.cargoType === CargoType.GeneralCargo ||
      this.railVisit?.cargoType === CargoType.GeneralCargo
    )
  }

  get vesselVisit() {
    return this.selectedVesselVisitItem?.data
  }

  get railVisit() {
    return this.railVisitStore.elements[this.railVisitId ?? -1]?.data
  }

  get orderUpdates() {
    if (this.isGeneralCargoVisit) return undefined
    return this.comparisonStore.orderUpdates
  }

  initAcceptUpdates = action(() => {
    const updates = this.comparisonStore.orderUpdates
    if (updates) {
      const newAcceptances: Dictionary<boolean> = {}
      ;[...updates.created, ...updates.amended, ...updates.deleted].forEach(cur => {
        newAcceptances[cur.containerNumber ?? ''] = true
      })
      this.acceptUpdates = newAcceptances
    }
  })

  get totalCount() {
    if (this.isGeneralCargoVisit) return this.generalCargoUploadOrders.length

    if (!this.orderUpdates) return 0

    return (
      this.orderUpdates.created.length +
      this.orderUpdates.deleted.length +
      this.orderUpdates.amended.length +
      this.orderUpdates.unchanged.length
    )
  }

  get issues() {
    return this.comparisonStore.issues
  }

  setOpen = async (open?: boolean) => {
    this.open = !!open
  }

  setVesselVisitId = async (id?: number) => {
    this.vesselVisitId = id

    if (id) await this.vesselVisitStore.fetchByIds([id])
  }

  setRailVisitId = async (id?: number) => {
    this.railVisitId = id

    if (id) await this.railVisitStore.fetchByIds([id])
  }

  setDirection = async (direction: CarrierVisitDirection) => {
    this.direction = direction
  }

  setIsRestow = (isRestow: boolean) => {
    this.isRestow = isRestow
  }

  setOrdersFromUpdate = (orders: UnitListDto[]) => {
    this.dataStore.setOrdersFromUpdate(orders)
  }

  setCustomer = (customer?: CompanyResponseDto) => {
    this.customer = customer
  }

  setUNLOCodes = (unloCodes: string[]) => {
    this.parsingStore.setUNLOCodes(unloCodes)
  }

  setFile = (file?: File) => {
    this.parsingStore.setFile(file)
  }

  setUploadFailedReason = (reason?: string) => {
    this.parsingStore.setUploadFailedReason(reason)
  }

  setActiveStep = (step: number) => {
    this.activeStep = step
  }

  parseOrderList = async (isRestow?: boolean) => {
    await this.parsingStore.parseOrderList(isRestow)
    this.initAcceptUpdates()
  }

  setAcceptUpdate = (containerNumber: string, accept: boolean, setRemainOnBoard?: boolean) => {
    this.acceptUpdates[containerNumber] = accept

    if (!accept && setRemainOnBoard) {
      const item = this.orderUpdates?.created.filter(i => i.containerNumber === containerNumber)[0]
      this.setAddToRemainOnBoard(item!)
    }
  }

  setAddToRemainOnBoard = (item: UnitListDto) => {
    const data = this.dataStore.remainOnBoard
    const exists = data.some(existingItem => existingItem.containerNumber === item.containerNumber)

    if (!exists) {
      data.push(item)
      this.dataStore.setRemainOnBoard(data)
    }
  }

  setShowVesselVisitMismatchWarning = (show: boolean) => {
    this.showVesselVisitMismatchWarning = show
  }

  setGeneralCargoUploadOrders = (orders: IGeneralCargoUploadOrderDto[]) => {
    this.generalCargoUploadOrders = orders
  }

  connectBaplieParser = (
    parseFunc: (file: File) => Promise<BaplieParseResult>,
    isEnabled: boolean,
  ) => {
    this.parsingStore.parseBaplie = parseFunc
    this.parsingStore.isBaplieParserReplacementFFEnabled = isEnabled
  }

  reset = () => {
    this.setActiveStep(0)
    this.setVesselVisitId()
    this.setDirection(CarrierVisitDirection.Inbound)
    this.setCustomer()

    this.parsingStore.reset()
    this.dataStore.reset()
    this.orderGroupStore.reset()
    this.setGeneralCargoUploadOrders([])
    this.isRestow = false
    this.setOpen(false)
  }
}
